import { useMutation, useQuery, gql, ApolloError } from "@apollo/client";
import {
  Box,
  Button,
  Collapse,
  Grid,
  Group,
  Loader,
  LoadingOverlay,
  Space,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import { useAuthContext } from "/@/shared/contexts/AuthContext";
import { graphql } from "/@/generated";
import { notifications } from "@mantine/notifications";
import { PhoneNumberInput } from "@jasper/forms";
import { useUserProfile } from "@jasper/use-cases";

const getDentistMainAddressSettings = graphql(`
  query GetDentistMainAddress {
    getDentistMainAddress {
      id
      recipient
      streetNumber
      streetName
      city
      postcode
      country
      name
    }
  }
`);

const updateDentistMainAddressSettings = graphql(`
  mutation UpdateDentistMainAddressSettings(
    $data: AddressUpdateInput!
    $where: AddressWhereUniqueInput!
  ) {
    updateAddress(data: $data, where: $where) {
      id
    }
  }
`);

const createDentistAddress = gql(`
  mutation createDentisteAddress(
    $args: AddressCreateInput!
  ){
    createAddress(args: $args){
      id
    }
  }
`);

type UpdateAddressData = {
  streetNumber?: string;
  streetName: string;
  city: string;
  postcode: string;
  name: string;
};

export const AccountMainInfoPage: FC = () => {
  const { user } = useAuthContext();
  const [isChangeAddress, setIsChangeAddress] = useState(false);
  const [phoneUpdate, setPhoneUpdate] = useState<string>();
  const [isPhoneNumberInvalid, setIsPhoneNumberInvalid] = useState(false);

  const { data: dentistMainAddress, loading: dentistMainAddressLoading } =
    useQuery(getDentistMainAddressSettings);
  const [updateAddress] = useMutation(updateDentistMainAddressSettings, {
    refetchQueries: ["GetDentistMainAddress"],
    awaitRefetchQueries: true,
  });
  const [createAddress] = useMutation(createDentistAddress, {
    refetchQueries: ["GetDentistMainAddress"],
    awaitRefetchQueries: true,
  });
  const { updateProfile, isUpdatingProfile } = useUserProfile();

  const { register, handleSubmit } = useForm<UpdateAddressData>({
    defaultValues: {
      streetNumber:
        dentistMainAddress?.getDentistMainAddress?.streetNumber ?? "",
      streetName: dentistMainAddress?.getDentistMainAddress?.streetName ?? "",
      city: dentistMainAddress?.getDentistMainAddress?.city ?? "",
      postcode: dentistMainAddress?.getDentistMainAddress?.postcode ?? "",
      name: dentistMainAddress?.getDentistMainAddress?.name ?? "",
    },
  });
  if (dentistMainAddressLoading) {
    return (
      <LoadingOverlay
        visible={dentistMainAddressLoading}
        zIndex={1000}
        overlayProps={{
          radius: "sm",
          blur: 2,
        }}
      >
        <Loader />
      </LoadingOverlay>
    );
  }

  const onSubmit = (data: UpdateAddressData) => {
    if (dentistMainAddress?.getDentistMainAddress?.id) {
      updateAddress({
        variables: {
          data: {
            streetNumber: {
              set: data.streetNumber,
            },
            streetName: {
              set: data.streetName,
            },
            city: { set: data.city },
            postcode: {
              set: data.postcode,
            },
          },
          where: {
            id: dentistMainAddress?.getDentistMainAddress.id,
          },
        },
      })
        .then(() => {
          notifications.show({
            color: "green",
            message: "Adresse modifiée avec succès",
          });
        })
        .finally(() => setIsChangeAddress(false));
    } else {
      createAddress({
        variables: {
          args: {
            streetNumber: data.streetNumber,
            streetName: data.streetName,
            city: data.city,
            postcode: data.postcode,
            name: data.name,
            user: {
              connect: {
                id: user?.id,
              },
            },
          },
        },
      })
        .then(() => {
          notifications.show({
            color: "green",
            message: "Adresse modifiée avec succès",
          });
        })
        .finally(() => setIsChangeAddress(false));
    }
  };

  return (
    <Box m="xl">
      <Title order={4}>Mon profil</Title>
      <Space h="md" />
      <Grid>
        <Grid.Col span={3}>
          <Text>Praticien</Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <TextInput
            readOnly
            value={"Dr. " + user?.firstName + " " + user?.lastName}
            disabled
          />
        </Grid.Col>
        <Grid.Col span={3}>
          <Text>Email</Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <TextInput readOnly value={user?.email} disabled />
        </Grid.Col>
        <Grid.Col span={3}>
          <Text>Telephone</Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <PhoneNumberInput
            onChange={setPhoneUpdate}
            value={phoneUpdate ?? user?.phoneNumber}
            error={
              isPhoneNumberInvalid
                ? "Le numéro de téléphone est invalide"
                : undefined
            }
          />
        </Grid.Col>
      </Grid>
      <Space h="xs" />
      <Group justify="flex-end">
        <Button
          disabled={
            phoneUpdate === undefined || phoneUpdate === user?.phoneNumber
          }
          onClick={async () => {
            setIsPhoneNumberInvalid(false);
            if (
              phoneUpdate !== undefined &&
              phoneUpdate !== user?.phoneNumber
            ) {
              await updateProfile({
                catchInputErrors: true,
                data: {
                  phoneNumber: phoneUpdate,
                },
                onError: error => {
                  if (
                    error instanceof ApolloError &&
                    error.cause?.message === "Invalid phone number format"
                  ) {
                    setIsPhoneNumberInvalid(true);
                  }
                },
              });
            }
          }}
          loading={isUpdatingProfile}
        >
          Sauvegarder mon profil
        </Button>
      </Group>
      <Space h="md" />
      <Title order={4}>Mon adresse</Title>
      <Space h="md" />
      <Grid>
        <Grid.Col span={3}>
          <Text>Adresse Principale</Text>
        </Grid.Col>
        <Grid.Col span={9}>
          <TextInput
            style={{
              pointerEvents: "none",
            }}
            readOnly
            value={
              dentistMainAddress
                ? dentistMainAddress?.getDentistMainAddress.streetNumber +
                  " " +
                  dentistMainAddress?.getDentistMainAddress.streetName +
                  ", " +
                  dentistMainAddress?.getDentistMainAddress.postcode +
                  " " +
                  dentistMainAddress?.getDentistMainAddress.city
                : ""
            }
          />
        </Grid.Col>
      </Grid>
      <Space h="md" />

      {isChangeAddress !== true && (
        <Group justify="flex-end">
          <Button onClick={() => setIsChangeAddress(true)}>
            Modifier mon adresse
          </Button>
        </Group>
      )}
      <Collapse expanded={isChangeAddress}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <TextInput
            {...register("name", {
              required: true,
            })}
            label="Nom"
            size="sm"
            required={true}
          />
          <TextInput
            {...register("streetNumber", { required: true })}
            label="Numéro de rue"
            size="sm"
            required={true}
          />
          <TextInput
            {...register("streetName", { required: true })}
            label="Nom de rue"
            size="sm"
            required={true}
          />
          <TextInput
            {...register("city", {
              required: true,
            })}
            label="Ville"
            size="md"
            required={true}
          />
          <TextInput
            {...register("postcode", {
              required: true,
            })}
            label="Code postal"
            size="md"
            required={true}
          />
          <Space h="md" />
          <Group justify="flex-end">
            <Button type="submit">Modifier</Button>
            <Button
              color="greyScale.5"
              onClick={() => setIsChangeAddress(false)}
            >
              Annuler
            </Button>
          </Group>
        </form>
      </Collapse>
    </Box>
  );
};
